/**
 * Central storage-path validators for Supabase Storage buckets.
 *
 * Used by both the frontend and (mirrored in
 * `supabase/functions/_shared/storagePaths.ts`) by edge functions.
 *
 * Goal: prevent path traversal and bucket-scope escape before any
 * `.upload()`, `.createSignedUrl(s)()`, or `.remove()` call. Backend
 * validation is authoritative; frontend validation is defense-in-depth.
 *
 * Allowed shapes:
 *   - avatars:        `<uuid>/avatar.(jpg|jpeg|png|webp)`
 *   - spot-captures:  `<session-uuid>/<media-uuid>.<ext>`
 *                     ext ∈ jpg|jpeg|png|webp|heic|m4a|mp3|wav|ogg|webm|mp4
 *
 * Rejected (regardless of bucket):
 *   - empty / non-string
 *   - leading slash, backslash, double slash
 *   - `..` segments
 *   - any percent-encoded byte (`%xx`)
 *   - control chars / whitespace
 *   - query strings (`?…`) or fragments (`#…`)
 *   - more than the expected number of segments
 *   - unexpected extensions
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const AVATAR_BUCKET = "avatars";
export const SPOT_CAPTURES_BUCKET = "spot-captures";

export const AVATAR_ALLOWED_EXT = ["jpg", "jpeg", "png", "webp"] as const;
export const SPOT_CAPTURE_ALLOWED_EXT = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "heic",
  "m4a",
  "mp3",
  "wav",
  "ogg",
  "webm",
  "mp4",
] as const;

/** Generic shape checks shared by every bucket validator. */
function failsBaseChecks(path: string): string | null {
  if (typeof path !== "string") return "not_a_string";
  if (path.length === 0 || path.length > 256) return "length";
  if (path !== path.trim()) return "whitespace";
  if (/[\u0000-\u001f\u007f]/.test(path)) return "control_char";
  if (path.includes("\\")) return "backslash";
  if (path.startsWith("/")) return "leading_slash";
  if (path.includes("//")) return "double_slash";
  if (path.includes("?") || path.includes("#")) return "query_or_fragment";
  if (/%[0-9a-fA-F]{2}/.test(path)) return "percent_encoded";
  // Per-segment `..` (also catches `.` only paths).
  for (const seg of path.split("/")) {
    if (seg === "" || seg === "." || seg === "..") return "dot_segment";
  }
  return null;
}

export function isValidAvatarPath(path: string): boolean {
  if (failsBaseChecks(path)) return false;
  const parts = path.split("/");
  if (parts.length !== 2) return false;
  const [folder, file] = parts;
  if (!UUID_RE.test(folder)) return false;
  const m = /^avatar\.([a-z0-9]+)$/i.exec(file);
  if (!m) return false;
  const ext = m[1].toLowerCase();
  return (AVATAR_ALLOWED_EXT as readonly string[]).includes(ext);
}

export function assertValidAvatarPath(path: string): void {
  if (!isValidAvatarPath(path)) {
    throw new Error(`invalid_avatar_path`);
  }
}

/** Build a guaranteed-safe avatar path from server-trusted inputs. */
export function buildAvatarPath(
  userId: string,
  ext: (typeof AVATAR_ALLOWED_EXT)[number],
): string {
  if (!UUID_RE.test(userId)) throw new Error("invalid_user_id");
  if (!(AVATAR_ALLOWED_EXT as readonly string[]).includes(ext)) {
    throw new Error("invalid_avatar_ext");
  }
  return `${userId}/avatar.${ext}`;
}

export function isValidSpotCapturePath(path: string): boolean {
  if (failsBaseChecks(path)) return false;
  const parts = path.split("/");
  if (parts.length !== 2) return false;
  const [sessionId, file] = parts;
  if (!UUID_RE.test(sessionId)) return false;
  const m = /^([0-9a-f-]{36})\.([a-z0-9]+)$/i.exec(file);
  if (!m) return false;
  if (!UUID_RE.test(m[1])) return false;
  const ext = m[2].toLowerCase();
  return (SPOT_CAPTURE_ALLOWED_EXT as readonly string[]).includes(ext);
}

export function assertValidSpotCapturePath(path: string): void {
  if (!isValidSpotCapturePath(path)) {
    throw new Error("invalid_spot_capture_path");
  }
}

/** Build a guaranteed-safe spot-capture path. `mediaId` should be a fresh UUID. */
export function buildSpotCapturePath(
  sessionId: string,
  mediaId: string,
  ext: string,
): string {
  const lower = ext.toLowerCase();
  const path = `${sessionId}/${mediaId}.${lower}`;
  assertValidSpotCapturePath(path);
  return path;
}